import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components/native';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Form from '../../components/form/Form';
import FormButton from '../../components/form/FormButton';
import FormInput from '../../components/form/FormInput';
import { H2, B, P } from '../../components/Typography';
import Spacer from '../../components/Spacer';
import Link from '../../components/Link';
import useLogin from '../../hooks/user/useLogin';
import Colors from '../../constants/Colors';
import { ActivityIndicator, Platform, useWindowDimensions } from 'react-native';
import { TouchableOpacity } from 'react-native-gesture-handler';
import * as LocalAuthentication from 'expo-local-authentication';
import biometrics from '../../biometrics/biometrics';
import api from '../../api/api';
import { useUserContext } from '../../context/UserContext';
import FormCheckbox from '../../components/form/FormCheckbox';
import accessibilityTags from '../../../accessibilityTags';
import { useLogout } from '../../hooks/user/useLogout';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';

const StyledHorizontalTextView = styled(KeyboardAvoidingView)`
  flex: 1;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
`;

const StyledView = styled(KeyboardAvoidingView)`
  flex: 1;
  align-items: center;
`;

const StyledImageBackground = styled.ImageBackground`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const StyledLoginScrollView = styled.ScrollView<{ isDesktop: boolean }>`
  width: 100%;
  height: 100%;
  padding: ${p => (p.isDesktop ? 0 : 87)}px 24px 0px 24px;
`;

const StyledFormView = styled.View`
  width: 100%;
`;

const StyledLink = styled(Link)`
  text-align: center;
  width: 100%;
`;

const StyledP = styled(P)`
  text-align: center;
  color: ${Colors.error};
`;

const StyledLoginContainer = styled.View<{ isDesktop: boolean }>`
  align-items: ${p => (p.isDesktop ? 'center' : 'flex-start')};
  align-self: center;
  max-width: 438px;
  width: 100%;
`;

const StyledBiometricsIcon = styled.Image`
  height: 24px;
  width: 24px;
  background-color: transparent;
  tint-color: black;
`;

// People Science logo, centered at the bottom of the login screen.
const StyledPeopleScienceLogo = styled.Image`
  width: 200px;
  height: 42px;
  align-self: center;
  margin-top: 32px;
`;

const LoginScreen: React.FC = () => {
  const formikRef = useRef<FormikProps<any> | null>();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const { jwtToken, loadingUser, loadingError, login } = useLogin();
  const { user, updateUserSession } = useUserContext();
  const window = useWindowDimensions();
  const isDesktop = window.width >= 768;
  const [biometricState, setBiometricState] = useState<{
    type: number;
    enabled: boolean;
    enrolled: boolean;
  }>({ type: 1, enabled: false, enrolled: false });
  const [isCheckingBiometrics, setIsCheckingBiometrics] = useState(false);
  const route = useRoute<RouteProp<any>>();
  const { logout } = useLogout();
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState<boolean>();
  const redirectToTodoScreen = useCallback(
    () =>
      navigation.replace('DrawerNavigator', {
        screen: 'BottomTabNav',
        params: { screen: 'ToDoNav' }
      }),
    [navigation]
  );

  const handleWebRedirectAfterUserSetup = useCallback(async () => {
    if (Platform.OS !== 'web') return;
    const token = route.params?.token;
    if (token) {
      setIsAutoLoggingIn(true);
      await logout();
      api.setAccessToken(token);
      updateUserSession();
      redirectToTodoScreen();
      setIsAutoLoggingIn(false);
    } else if (user) {
      redirectToTodoScreen();
    }
  }, [
    logout,
    redirectToTodoScreen,
    route.params?.token,
    updateUserSession,
    user
  ]);

  useEffect(() => {
    handleWebRedirectAfterUserSetup();
  }, [handleWebRedirectAfterUserSetup]);

  useEffect(() => {
    if (Platform.OS === 'web') return;
    (async () => {
      const biometricTypes =
        await LocalAuthentication.supportedAuthenticationTypesAsync();
      const hasEnrolledBiometric = await LocalAuthentication.isEnrolledAsync();
      const enabled = await biometrics.getBiometricLogin();
      setBiometricState({
        type: biometricTypes[0],
        enabled: enabled === 'true',
        enrolled: hasEnrolledBiometric
      });
    })();
  }, []);

  useEffect(() => {
    formikRef.current?.setSubmitting(loadingUser);
  }, [loadingUser]);

  useEffect(() => {
    if (jwtToken?.user) {
      (async () => {
        if (formikRef.current?.values.enableBiometrics) {
          await biometrics.setBiometricLogin(true);
        }
        redirectToTodoScreen();
      })();
    }
  }, [jwtToken?.user, redirectToTodoScreen]);

  return Platform.OS === 'web' && (isAutoLoggingIn || user) ? (
    <ActivityIndicator size='large' color={Colors.primary} />
  ) : (
    <StyledView behavior='padding'>
      <StyledImageBackground
        resizeMode='stretch'
        source={require('../../assets/intro/background.png')}
      />
      <StyledLoginScrollView
        isDesktop={isDesktop}
        contentContainerStyle={
          isDesktop ? { flexGrow: 1, justifyContent: 'center' } : {}
        }
      >
        <StyledLoginContainer isDesktop={isDesktop}>
          <H2
            testID={accessibilityTags.loginWelcomeID}
            accessibilityLabel={accessibilityTags.loginWelcomeID}
            color='dark'
          >
            Welcome To Chloe!
          </H2>
          <Spacer size={4} />
          <StyledFormView>
            <Formik
              innerRef={formik => (formikRef.current = formik)}
              initialValues={{
                email: '',
                password: '',
                enableBiometrics: false
              }}
              validationSchema={Yup.object().shape({
                email: Yup.string().email().required('Email is required.'),
                password: Yup.string().required('Password is required.'),
                enableBiometrics: Yup.bool()
              })}
              onSubmit={formValues => {
                login({
                  email: formValues.email,
                  password: formValues.password
                });
              }}
            >
              <Form>
                <FormInput
                  endElement={
                    biometricState.enabled && biometricState.enrolled ? (
                      <TouchableOpacity
                        onPress={async () => {
                          setIsCheckingBiometrics(true);
                          const biometricAuth =
                            await LocalAuthentication.authenticateAsync({
                              promptMessage: 'Login with Biometrics',
                              cancelLabel: 'Cancel',
                              disableDeviceFallback: false
                            });
                          if (biometricAuth.success) {
                            const accessToken =
                              await biometrics.getBiometricAccessToken();
                            if (accessToken) {
                              await api.setAccessToken(accessToken);
                              await biometrics.removeBiometricAccessToken();
                              await updateUserSession();
                              navigation.replace('DrawerNavigator', {
                                screen: 'BottomTabNav',
                                params: { screen: 'ToDoNav' }
                              });
                            }
                          }
                          setIsCheckingBiometrics(false);
                        }}
                      >
                        <StyledBiometricsIcon
                          source={
                            biometricState.type === 1
                              ? require('../../assets/sign-up/fingerprint.png')
                              : require('../../assets/sign-up/face-id.png')
                          }
                        />
                      </TouchableOpacity>
                    ) : undefined
                  }
                  keyboardType='email-address'
                  autoCapitalize='none'
                  autoCorrect={false}
                  accessibilityLabel={accessibilityTags.loginEmailID}
                  testID={accessibilityTags.loginEmailID}
                  name='email'
                  label='Email'
                  placeholder='Email address'
                />
                <FormInput
                  secureTextEntry
                  accessibilityLabel={accessibilityTags.loginPasswordID}
                  testID={accessibilityTags.loginPasswordID}
                  name='password'
                  returnKeyType='go'
                  submitOnReturn={true}
                  label='Password'
                  placeholder='Password'
                />
                {!biometricState.enabled && biometricState.enrolled && (
                  <FormCheckbox
                    name='enableBiometrics'
                    label='Enable Biometrics'
                    dark
                  />
                )}
                {loadingError && (
                  <>
                    <Spacer size='small' />
                    <StyledP
                      accessibilityLabel={accessibilityTags.errorLabelID}
                      testID={accessibilityTags.errorLabelID}
                    >
                      {loadingError.statusCode === 401
                        ? 'Incorrect email or password. Please try again.'
                        : loadingError.message}
                    </StyledP>
                  </>
                )}
                <Spacer size={4} />
                <FormButton
                  testID={accessibilityTags.loginButtonID}
                  disabled={isCheckingBiometrics}
                  accessibilityLabel={accessibilityTags.loginButtonID}
                >
                  Log In
                </FormButton>
              </Form>
            </Formik>
            <Spacer size={5} />
            <StyledLink
              inverse
              accessibilityLabel={accessibilityTags.loginForgotPasswordID}
              testID={accessibilityTags.loginForgotPasswordID}
              onPress={() => {
                navigation.navigate('ForgotPasswordScreen');
              }}
            >
              Forgot Password
            </StyledLink>
            <Spacer size={4} />
            <StyledHorizontalTextView>
              <B center color='dark'>
                Don&apos;t Have An Account?{' '}
              </B>
              <Link
                inverse
                accessibilityLabel={accessibilityTags.loginSignUpButtonID}
                testID={accessibilityTags.loginSignUpButtonID}
                onPress={() => {
                  navigation.navigate('SignUpScreen');
                }}
              >
                Sign Up
              </Link>
            </StyledHorizontalTextView>
            {!isDesktop && <Spacer size={15} />}
          </StyledFormView>
        </StyledLoginContainer>
        <StyledPeopleScienceLogo
          accessibilityLabel='People Science'
          resizeMode='contain'
          source={require('../../assets/intro/people-science-logo-horizontal.png')}
        />
        <Spacer size={6} />
      </StyledLoginScrollView>
    </StyledView>
  );
};

export default LoginScreen;
